<template>
  <section class="containerSpotLight">
    <section class="containerSpotLight__content">
      <div class="containerSpotLight__content--back">
        <div class="contentBack" @click="goBackHandler">
          <span class="grey-icon-arrow-back contentBack__icon"></span>
          <span class="contentBack__label bookFont">Back</span>
        </div>
      </div>
      <notSupportedMobile v-show="isMobileResolution" message="This device does not allow uploading."></notSupportedMobile>
      <section v-show="!isMobileResolution" v-if="loadPage" class="containerSpotLight__content--form">
        <span class="form__title">{{isAnEdition ? 'Edit' : 'New'}} Spotlight</span>
        <section class="form__body">
          <form class="form__body--main" @change="changeHandler">
            <inputGeneric
              label="TITLE"
              id="form_title"
              :maxLength="60"
              v-model="formFields.title"
              placeholder="Start typing..."
              :showErrors="showErrorsDraft || store.state.spotlight.showErrors"
              required>
            </inputGeneric>
            <section class="uploadContainer">
              <div class="uploadContainer__header">
                <span class="uploadContainer__header--title required">UPLOAD PHOTO</span>
              </div>
              <section class="uploadContainer__uploader">
                <spotlightImageUploader
                  :showErrors="store.state.spotlight.showErrors"
                  required
                  @touchedFile="changeHandler()"
                  @set-validation-active="setValidationActive">
                </spotlightImageUploader>
                <div class="uploadContainer__uploader--message">
                  <p>Upload photo that reflects your publication content in the application. Remember that only .png and .jpg format are allowed. <span class="attention"> This will call the user's attention.</span></p>
                  <p class="validation" :class="{'validationActive': validationActive}">Minimum resolution: 800px x 800px</p>
                </div>
              </section>
            </section>
            <htmlInput
              label="DESCRIPTION"
              ref="htmlInputRef"
              v-model="formFields.description"
              @update:modelValue="changeHandler"
              :showErrors="store.state.spotlight.showErrors"
              required>
            </htmlInput>
            <inputListMultipleValues
              label="TAGS"
              v-model:modelValue="formFields.tags"
              :getAsyncOptions="searchTags"
              :showLabelFunc="(option) => option"
              :showErrors="store.state.spotlight.showErrors"
              idProperty="id"
              allowUserValues
              :showArrowDropDown="false"
              :specialCharListToDetect="[',', ';', ' ']">
            </inputListMultipleValues>
            <inputList
              label="AUTHOR"
              v-model:modelValue="formFields.author"
              :getAsyncOptions="searchPeople"
              :showArrowDropDown="false"
              :showErrors="store.state.spotlight.showErrors"
              required
              disabled>
            </inputList>
          </form>
          <section class="form__body--secondary">
            <section class="publishContainer">
              <div class="publishContainer__header">
                <span class="publishContainer__header--title">PUBLISH</span>
              </div>
              <div class="publishContainer__date">
                <div class="publishContainer__date--status">
                  <span class="statusTitle">Status:</span>
                  <span class="statusLabel">{{formFields.status}}</span>
                </div>
                <div class="publishContainer__date--field">
                  <span class="label required">Publish Date</span>
                  <datePicker
                    v-model="formFields.startDate"
                    @update:modelValue="changeHandler()"
                    :showErrors="store.state.spotlight.showErrors"
                    required>
                  </datePicker>
                </div>
              </div>
              <div class="publishContainer__footer" :class="{'withoutDraft': !showSaveAsDraft}">
                <button class="publishContainer__footer--draftButton" v-if="showSaveAsDraft" :class="{'withSpinner': showDraftSpinner}" @click="saveAsDraft" :disabled="saveAsDraftDisabled">
                  <spinner v-if="showDraftSpinner" class="draftSpinner"/>
                  <span>Save as Draft</span>
                </button>
                <button class="publishContainer__footer--button" @click="publish" :class="{'withSpinner': showSpinner}">
                  <spinner :class="[ buttonLabel === 'Publish' ? 'publishSpinner' : 'scheduleSpinner']" v-if="showSpinner"/>
                  {{buttonLabel}}
                </button>
              </div>
            </section>
          </section>
        </section>
      </section>
      <skeleton-spotlight-form v-if="!loadPage" />
      <confirmationModal
        class="exitFormModal"
        ref="exitModalRef"
        title="Exit Editing"
        message="If you leave now you will lose everything."
        boldMessage="Do you still want to leave?"
        confirmText="YES"
        cancelText="CANCEL"
        @confirmAction="goBack(true)">
      </confirmationModal>
      <successModal
        ref="successModalRef"
        @close="goBack"
        :message="modalMessage.title"
        :additionalMessage="modalMessage.text"
        :icon="modalMessage.icon"
        :class="modalMessage.name">
      </successModal>
    </section>
    <transition name="toast">
      <toastMessage v-if="store.state.toastMessage.visible"/>
    </transition>
  </section>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import formConfig from './spotlightForm.json'
import notSupportedMobile from '@/components/notSupportedMobile/notSupportedMobile'
import inputGeneric from '@/components/form/inputGeneric/inputGeneric'
import htmlInput from '@/components/form/htmlInput/htmlInput'
import inputListMultipleValues from '@/components/form/inputListMultipleValues/inputListMultipleValues'
import inputList from '@/components/form/inputList/inputList'
import datePicker from '@/components/form/datePicker/datePicker'
import spinner from '@/components/spinner/spinner'
import confirmationModal from '@/components/confirmationModal/confirmationModal'
import successModal from '@/components/successModal/successModal'
import toastMessage from '@/components/toastMessage/toastMessage'
import spotlightImageUploader from '@/components/spotLights/spotlightImageUploader/spotlightImageUploader'
import moment from 'moment'
import skeletonSpotlightForm from '@/components/spotLights/skeletonSpotlightForm/skeletonSpotlightForm'

export default {
  name: 'spotlight',
  components: {
    notSupportedMobile,
    inputGeneric,
    htmlInput,
    inputListMultipleValues,
    inputList,
    datePicker,
    spinner,
    confirmationModal,
    successModal,
    toastMessage,
    spotlightImageUploader,
    skeletonSpotlightForm
  },
  setup (props) {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const isFormTouched = ref(false)
    const exitModalRef = ref(null)
    const successModalRef = ref(null)
    const isMobileResolution = ref(false)
    const loadPage = ref(false)
    const formFields = ref(JSON.parse(JSON.stringify(formConfig.formData)))
    const showSpinner = ref(false)
    const showDraftSpinner = ref(false)
    const htmlInputRef = ref(null)
    const modalMessage = ref(JSON.parse(JSON.stringify(formConfig.modalMessages.published)))
    const validationActive = ref(false)
    const showErrorsDraft = ref(false)
    const saveAsDraftDisabled = ref(false)

    /**
     * @description Checks if the current form is an edition
     */
    const isAnEdition = computed(() => {
      return route.params.spotlightId || store.state.spotlight.spotlightItem.id
    })

    /**
     * @description Checks if form has all required fields completed
     */
    const isFormComplete = computed(() => {
      return formFields.value.title.trim() && formFields.value.description && htmlInputRef.value.wordCounter && formFields.value.author && store.state.spotlight.imageId
    })

    /**
     * @description Checks if publish date is in the future (based on EST time)
     */
    const isScheduledDate = computed(() => {
      return store.getters['opportunities/getTimeDiference'](formFields.value.startDate, 'minutes') > 0
    })

    /**
     * @description Validate if display save as draft button
     */
    const showSaveAsDraft = computed(() => {
      return ['new', 'draft'].includes(formFields.value.status.toLowerCase())
    })

    /**
     * @description Label button to display
     */
    const buttonLabel = computed(() => {
      return showSaveAsDraft.value ? (isScheduledDate.value ? 'Schedule' : 'Publish') : 'Update'
    })

    onMounted(() => {
      resizeSetUp()
      handleResize()
      setInitialData()
    })

    /**
     * @description Gets and set the initial data to display in the form
     */
    function setInitialData () {
      if (isAnEdition.value) {
        store.dispatch({
          type: 'spotlight/getSpotlight',
          id: route.params.spotlightId
        }).then(
          (response) => {
            if (!response.success) {
              const routeData = router.resolve({ name: 'emptyState' })
              window.open(routeData.href, '_self')
            } else {
              store.commit({
                type: 'spotlight/SET_SPOTLIGHT_ITEM',
                spotlight: formatSpotlight(response)
              })
              if (response.imageId) {
                store.commit({
                  type: 'spotlight/SET_SPOTLIGHT_IMAGE_ID',
                  id: response.imageId
                })
                store.commit({
                  type: 'spotlight/UPDATE_UPLOAD_SPOTLIGHT_LOGO_INFO',
                  info: { status: 'uploaded' }
                })
              }
              formFields.value = store.state.spotlight.spotlightItem
              loadPage.value = true
            }
          }
        )
      } else {
        formFields.value.author = userFormField({
          eccode: store.state.dataUserLogin.ecCode,
          email: store.state.dataUserLogin.email,
          name: store.state.dataUserLogin.name
        })
        formFields.value.startDate = moment(new Date()).format('YYYY-MM-DD')
        setTimeout(() => {
          loadPage.value = true
        }, 1000)
      }
    }

    /**
     * @description Format user object
     * @param name name of user
     * @param email email of user
     * @param ecCoce ecCoce of user
     * @param eccode eccode of user
     */
    function userFormField ({ name, email, ecCode, eccode }) {
      return {
        name,
        email,
        ecCode: ecCode || eccode,
        value: `${name} (${email})`
      }
    }

    /**
     * @description Format spotlight fields to fill the form
     * @param spotlight the spotlight data
     */
    function formatSpotlight (spotlight) {
      spotlight.tags = spotlight.tags.map(data => ({ value: data, id: data }))
      spotlight.author = userFormField(spotlight.author)
      return spotlight
    }

    /**
     * @description Go to spotlights admin
     * @param backButton Flag to know if back button was clicked
     */
    function goToSpotlights (backButton = false) {
      if (isAnEdition.value && backButton) {
        window.open(document.referrer, '_self')
      } else {
        const routeData = router.resolve({ name: 'spotlights' })
        window.open(routeData.href, '_self')
      }
    }

    /**
     * @description Checks the size of the screen
     */
    function handleResize () {
      window.addEventListener('resize', function (e) {
        resizeSetUp()
      })
    }

    /**
     * @description Checks if screen has mobile resolution
     */
    function resizeSetUp () {
      isMobileResolution.value = window.innerWidth <= 1024
    }

    /**
     * @description Determines if user can go back or if a modal should be shown.
     */
    function goBackHandler () {
      if (isFormTouched.value) {
        exitModalRef.value.open()
      } else {
        goBack(true)
      }
    }

    /**
     * @description Go to go grey home page
     * @param backButton Flag to know if back button was clicked
     */
    function goBack (backButton = false) {
      manageBeforeUnloadListener(false)
      goToSpotlights(backButton)
    }

    /**
     * @description Manages to set the form as touched.
     */
    function changeHandler () {
      if (!isFormTouched.value) {
        manageBeforeUnloadListener(false)
        manageBeforeUnloadListener()
        isFormTouched.value = true
      }
    }

    /**
     * @description Adds/Removes listener for beforeunload event.
     * @param addEvent Flag to know if listener should be added or removed.
     */
    function manageBeforeUnloadListener (addEvent = true) {
      window[addEvent ? 'addEventListener' : 'removeEventListener']('beforeunload', listenerChangePage)
    }

    /**
     * @description Listens when the users wants to change the page and shows a prevent modal.
     * @param {event} event fired by the user.
     */
    function listenerChangePage (event) {
      event.preventDefault()
      event.returnValue = ''
    }

    /**
     * @description Searches the tags based on a search term.
     * @param {searchTerm} searchTerm Term to search tags.
     */
    function searchTags (searchTerm) {
      return store.dispatch({
        type: 'article/searchTag',
        searchTerm: searchTerm
      })
    }

    /**
     * @description Searches users based on search term.
     * @param searchTerm Term to search collaborators.
     */
    function searchPeople (searchTerm) {
      return store.dispatch({
        type: 'opportunities/searchUser',
        searchTerm: searchTerm
      })
    }

    /**
     * @description Display success modal
     * @param type the type of modal to show
     */
    function displaySuccessModal (type) {
      modalMessage.value = formConfig.modalMessages[type]
      if (type === 'scheduled') {
        modalMessage.value.text = modalMessage.value.text.replace('${}', moment(formFields.value.startDate).format('MMM D YYYY'))
      }
      successModalRef.value.open()
    }

    /**
     * @description Validate if spotlight can be published
     */
    function prePublishValidation () {
      setShowErrors()

      if (!isFormComplete.value) {
        displayToastMessage('publishValidation')
        return false
      }

      if (store.state.spotlight.fileInfo.status === 'uploading') {
        displayToastMessage('fileUploading')
        return false
      }

      return true
    }

    /**
     * @description Calls service to publish spotlight
     */
    function publish () {
      if (prePublishValidation()) {
        showSpinner.value = true
        store.dispatch({
          type: 'spotlight/saveSpotlight',
          publish: true,
          data: generateSpotlightBody()
        }).then(response => {
          showSpinner.value = false
          if (response.success) {
            displaySuccessModal(showSaveAsDraft.value ? (isScheduledDate.value ? 'scheduled' : 'published') : 'updated')
            const status = isScheduledDate.value ? 'Scheduled' : 'Published'
            formFields.value.status = status
            store.commit({
              type: 'spotlight/SET_SPOTLIGHT_ITEM_STATUS',
              status: status
            })
          } else {
            displayToastMessage('requestError')
          }
        }).catch(() => {
          showSpinner.value = false
          displayToastMessage('requestError')
        })
      }
    }

    /**
     * @description Generates the data object to send to publish service
     */
    function generateSpotlightBody () {
      const { value: valueAuthor, ...author } = formFields.value.author
      const body = {
        id: isAnEdition.value ? store.state.spotlight.spotlightItem.id : '',
        title: formFields.value.title,
        description: formFields.value.description,
        tags: formFields.value.tags.map(data => data.value),
        author,
        startDate: formFields.value.startDate,
        imageId: store.state.spotlight.imageId
      }
      return body
    }

    /**
     * @description Set show errors
     * @param showErrors boolean flag
     */
    function setShowErrors (showErrors = true) {
      store.commit({
        type: 'spotlight/SET_SPOTLIGHT_FORM_ERRORS',
        showErrors: showErrors
      })
    }

    /**
     * @description show toast message during 5 seconds
     * @param type type of toast
     * @param error error code
     */
    function displayToastMessage (type, error = '') {
      store.commit({
        type: 'SET_TOAST_MESSAGE',
        toastMessage: { visible: true, type: type, error: error }
      })
      setTimeout(() => {
        resetToastMessage()
      }, 5000)
    }

    /**
     * @description reset toast message
     */
    function resetToastMessage () {
      store.commit({
        type: 'SET_TOAST_MESSAGE',
        toastMessage: { visible: false, type: '', error: '' }
      })
    }

    /**
     * @description set validation active flag
     * @param flag boolean flag
     */
    function setValidationActive (flag) {
      validationActive.value = flag
    }

    /**
     * @description Save spotlight information as a draft
     */
    function saveAsDraft () {
      showErrorsDraft.value = true
      resetToastMessage()
      setShowErrors(false)
      if (saveAsDraftValidation()) {
        saveAsDraftDisabled.value = true
        showDraftSpinner.value = true
        store.dispatch({
          type: 'spotlight/saveSpotlight',
          publish: false,
          data: generateSpotlightBody()
        }).then(response => {
          showDraftSpinner.value = false
          if (response.success) {
            manageBeforeUnloadListener(false)
            isFormTouched.value = false
            formFields.value.status = 'Draft'
            store.commit({
              type: 'spotlight/SET_SPOTLIGHT_ITEM_STATUS',
              status: 'Draft',
              id: response.id
            })
            displayToastMessage('draftSuccess')
          } else {
            displayToastMessage('requestError')
          }
          saveAsDraftDisabled.value = false
        }).catch(() => {
          showDraftSpinner.value = false
          displayToastMessage('requestError')
          saveAsDraftDisabled.value = false
        })
      }
    }

    /**
     * @description Validations before save as draft
     */
    function saveAsDraftValidation () {
      const validation = formFields.value.title.trim() && store.state.spotlight.fileInfo.status !== 'uploading'
      if (!formFields.value.title) {
        document.getElementById('form_title').focus()
        displayToastMessage('publishValidation')
      }
      if (store.state.spotlight.fileInfo.status === 'uploading') {
        displayToastMessage('fileUploading')
      }
      return validation
    }

    return {
      store,
      router,
      route,
      isFormTouched,
      exitModalRef,
      successModalRef,
      isMobileResolution,
      loadPage,
      formFields,
      showSpinner,
      htmlInputRef,
      isAnEdition,
      changeHandler,
      goBackHandler,
      searchTags,
      searchPeople,
      modalMessage,
      isFormComplete,
      isScheduledDate,
      displaySuccessModal,
      publish,
      validationActive,
      setValidationActive,
      goBack,
      showSaveAsDraft,
      showDraftSpinner,
      saveAsDraft,
      showErrorsDraft,
      buttonLabel,
      skeletonSpotlightForm,
      saveAsDraftDisabled
    }
  }
}
</script>
